import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { SidebarComponent } from '../../sidebar.component';
import { CartItem, CartService } from '../../../../../../core/services/cart.service';
import { AuthService } from '../../../../../../core/services/auth.service';
import { CheckoutService } from '../../../../../../core/services/checkout.service';
import { environment } from '../../../../../../../environments/environment';

@Component({
  selector: 'app-cart-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent],
  templateUrl: './cart-sidebar.component.html',
  styleUrls: ['./cart-sidebar.component.scss'],
})
export class CartSidebarComponent {

  private cartService = inject(CartService);
  private checkoutService = inject(CheckoutService);
  private http = inject(HttpClient);
  authService = inject(AuthService);

  @Input() isOpen = false;
  @Input() items: CartItem[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() editPersonalization = new EventEmitter<CartItem>();

  isProcessing = false;
  checkoutError: string | null = null;

  // Discount
  showDiscountInput = false;
  discountCode = '';
  discountError = '';
  appliedDiscount: { code: string; percentage: number; discountAmount: number } | null = null;

  onClose() {
    this.close.emit();
  }

  increase(item: CartItem) {
    this.cartService.increase(item.cartId);
  }

  decrease(item: CartItem) {
    this.cartService.decrease(item.cartId);
  }

  remove(item: CartItem) {
    this.cartService.remove(item.cartId);
  }

  get subtotal(): number {
    return this.items.reduce((acc, item) => {
      const customCost = (item.customizationCount ?? 0) * 10;
      return acc + (item.price + customCost) * item.qty;
    }, 0);
  }

  get discount(): number {
    return this.appliedDiscount?.discountAmount ?? 0;
  }

  get total(): number {
    return this.subtotal - this.discount;
  }

  validateDiscount(): void {
    if (!this.discountCode.trim()) return;
    this.discountError = '';

    const token = this.authService.getToken();
    if (!token) {
      this.authService.showLoginModal.set(true);
      return;
    }

    const http = this.http;
    http.post<any>(`${environment.apiUrl}/discount/validate`, { code: this.discountCode.trim() }, {
      headers: { Authorization: `Bearer ${token}` }
    }).subscribe({
      next: res => {
        if (res.success) {
          const percentage = res.data.discountPercentage;
          const discountAmount = Math.round(this.subtotal * (percentage / 100) * 100) / 100;
          this.appliedDiscount = {
            code: res.data.code,
            percentage,
            discountAmount,
          };
        } else {
          this.discountError = res.message || 'Código inválido';
        }
      },
      error: (err) => {
        this.discountError = err?.error?.message || 'Código inválido';
      }
    });
  }

  private getApiUrl(): string {
    return environment.apiUrl;
  }

  removeDiscount(): void {
    this.appliedDiscount = null;
    this.discountCode = '';
    this.discountError = '';
  }

  checkout(): void {
    if (!this.authService.isLoggedIn()) {
      this.authService.showLoginModal.set(true);
      return;
    }

    const profile = this.authService.customerProfile();
    if (!profile) return;
    if (this.isProcessing) return;

    this.checkoutError = null;

    const addressParts = [
      profile.address,
      profile.district,
      profile.province,
      profile.department,
    ].filter(Boolean);

    const products = this.items.map(item => {
      const customCost = (item.customizationCount ?? 0) * 10;
      return {
        productId: item.publicId,
        name: item.name,
        price: item.price + customCost,
        basePrice: item.oldPrice,
        discountPercentage: item.discountPercentage,
        imageUrl: item.image,
        quantity: item.qty,
        attributes: {
          select: item.orderSelectAttrs ?? [],
          number: item.orderNumberAttrs ?? [],
          text: item.orderTextAttrs ?? [],
        },
      };
    });

    const originalTotal = this.total;

    const orderPayload = {
      products,
      client: {
        id: profile.publicId,
        name: `${profile.firstName} ${profile.lastName}`,
        dni: profile.dni,
        email: profile.email,
        phone: profile.phone,
        address: addressParts.length > 0 ? addressParts.join(', ') : null,
      },
      salesChannel: {
        code: 'WEB',
        name: 'Página web',
      },
      orderCode: `WEB-${Date.now()}`,
      payment: {
        fullPayment: true,
      },
      delivery: {
        allDelivered: true,
      },
      discount: this.appliedDiscount ? { code: this.appliedDiscount.code, percentage: this.appliedDiscount.percentage, discountAmount: this.appliedDiscount.discountAmount } : null,
      totals: {
        originalTotal: this.subtotal,
        finalTotal: this.total,
      },
    };

    console.log('🛒 Orden:', JSON.stringify(orderPayload, null, 2));

    this.isProcessing = true;
    this.checkoutService.createCheckout(orderPayload).subscribe({
      next: res => {
        this.isProcessing = false;
        if (res.success) {
          this.checkoutService.redirectToCheckout(res.data.initPoint, res.data.sandboxInitPoint);
        }
      },
      error: (err) => {
        this.isProcessing = false;
        this.checkoutError = err?.error?.message ?? 'Error al procesar el pago';
      }
    });
  }

  editCustomization(item: CartItem): void {
    this.editPersonalization.emit(item);
  }
}
